/**
 * EJ Tech V2 design tokens.
 *
 * Anchored on the existing EJ Tech logo (emerald cube + tangerine highlight),
 * with a Young Post Club-inspired typographic hierarchy:
 * uppercase Montserrat eyebrows over Inter body text, generous whitespace,
 * pill-shaped tags, hover-lift cards.
 *
 * Do NOT import CORE_COLORS / CORE_FONT (HKEJ V2) or LJV2_COLORS / LJV2_FONT (LJ V2).
 * EJ Tech V2 is intentionally its own visual system.
 */

export const EJTECH_COLORS = {
  // brand — sourced from the ej-tech logo
  primary: "#10A85A", // logo emerald — links, CTAs, eyebrows
  primaryDark: "#0E8C4A",
  primarySoft: "#E8F6EE",
  accent: "#F37021", // logo tangerine — featured tags, highlights
  accentDark: "#D85A14",
  accentSoft: "#FFEFE2",

  // ink
  ink: "#0B1220",
  ink2: "#1F2937",
  muted: "#6B7280",
  mutedSoft: "#9CA3AF",

  // surfaces
  surface: "#FFFFFF",
  surfaceAlt: "#F5F9F4", // soft green-tinted band
  surfaceWarm: "#FFF8F1", // soft tangerine-tinted band
  surfaceDark: "#0B1220", // newsletter / footer

  // hairlines
  hairline: "#E5E7EB",
  hairlineSoft: "rgba(0,0,0,0.06)",
  hairlineOnDark: "rgba(255,255,255,0.14)",

  // states
  textOnDark: "#FFFFFF",
  textOnDarkMuted: "rgba(255,255,255,0.62)",
  danger: "#DC2626",
} as const;

export const EJTECH_FONT = {
  display: `Montserrat, "Noto Sans TC", ui-sans-serif, system-ui, sans-serif`,
  body: `Inter, "Noto Sans TC", ui-sans-serif, system-ui, sans-serif`,
  mono: `"JetBrains Mono", ui-monospace, SFMono-Regular, Menlo, monospace`,
} as const;

/**
 * Reusable type recipes. Spread into a `style={{}}` to apply.
 * Keep numeric where possible so Tailwind/JSX can consume directly.
 */
export const EJTECH_TYPE = {
  eyebrow: {
    fontFamily: EJTECH_FONT.display,
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: "0.18em",
    textTransform: "uppercase" as const,
  },
  eyebrowLg: {
    fontFamily: EJTECH_FONT.display,
    fontSize: 13,
    fontWeight: 800,
    letterSpacing: "0.2em",
    textTransform: "uppercase" as const,
  },
  sectionLabel: {
    fontFamily: EJTECH_FONT.display,
    fontSize: 14,
    fontWeight: 800,
    letterSpacing: "0.16em",
    textTransform: "uppercase" as const,
  },
  navLink: {
    fontFamily: EJTECH_FONT.display,
    fontSize: 13,
    fontWeight: 600,
    letterSpacing: "0.06em",
  },
  hero: {
    fontFamily: EJTECH_FONT.display,
    fontWeight: 800,
    letterSpacing: "-0.01em",
    lineHeight: 1.06,
  },
  h2: {
    fontFamily: EJTECH_FONT.display,
    fontSize: 32,
    fontWeight: 800,
    letterSpacing: "-0.005em",
    lineHeight: 1.15,
  },
  h3: {
    fontFamily: EJTECH_FONT.display,
    fontSize: 24,
    fontWeight: 700,
    lineHeight: 1.2,
  },
  cardTitle: {
    fontFamily: EJTECH_FONT.display,
    fontSize: 18,
    fontWeight: 700,
    lineHeight: 1.3,
  },
  cardTitleLg: {
    fontFamily: EJTECH_FONT.display,
    fontSize: 22,
    fontWeight: 800,
    lineHeight: 1.22,
  },
  cardTitleXl: {
    fontFamily: EJTECH_FONT.display,
    fontSize: 28,
    fontWeight: 800,
    lineHeight: 1.18,
  },
  body: {
    fontFamily: EJTECH_FONT.body,
    fontSize: 16,
    lineHeight: 1.6,
    fontWeight: 400,
  },
  bodySm: {
    fontFamily: EJTECH_FONT.body,
    fontSize: 14,
    lineHeight: 1.55,
    fontWeight: 400,
  },
  meta: {
    fontFamily: EJTECH_FONT.body,
    fontSize: 12,
    fontWeight: 500,
    lineHeight: 1.4,
  },
  pill: {
    fontFamily: EJTECH_FONT.display,
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: "0.12em",
    textTransform: "uppercase" as const,
  },
} as const;

/**
 * Category → palette accent map. Keeps category pills feeling lively and
 * varied across the home page (rather than all-emerald), while staying inside
 * the brand vocabulary (emerald + tangerine + supporting tints).
 */
export const EJTECH_CATEGORY_TONE: Record<
  string,
  { fg: string; bg: string }
> = {
  CEO_AI: { fg: "#10A85A", bg: "#E8F6EE" },
  科技動態: { fg: "#0B1220", bg: "#F1F5F9" },
  人物專訪: { fg: "#D85A14", bg: "#FFEFE2" },
  專家分析: { fg: "#0E8C4A", bg: "#E8F6EE" },
  創新生態圈: { fg: "#0B1220", bg: "#FFEFE2" },
  活動盛事: { fg: "#D85A14", bg: "#FFF8F1" },
  五次元: { fg: "#10A85A", bg: "#E8F6EE" },
  創科新視野: { fg: "#0B1220", bg: "#E8F6EE" },
  全民大數據: { fg: "#D85A14", bg: "#FFEFE2" },
  科網人語: { fg: "#10A85A", bg: "#E8F6EE" },
  智情筆報: { fg: "#0B1220", bg: "#F1F5F9" },
};
